import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, PencilSimple, Coins, ChartLine, Users, Package,
  MagnifyingGlass, CircleNotch, X, Check, Warning, ArrowsClockwise,
  CloudArrowUp, Lightning,
} from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Button } from "../components/ui/button";

// Admin-only billing catalog + credit ledger console. All mutations are
// re-verified server-side; this UI just orchestrates the calls.

const errMsg = (e, fb) => {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : fb;
};

const inputCls = "w-full rounded-lg bg-black/40 border border-white/10 px-3 py-2.5 text-sm outline-none focus:border-amber-400/60";
const money = (n) => (Number(n) || 0).toFixed(2);
const dt = (s) => (s ? new Date(s).toLocaleString() : "—");

// ---------------- Plan editor dialog ----------------

function PlanDialog({ dialog, onClose, onSaved }) {
  const [f, setF] = useState({
    plan_id: "", name: "", tier: "", billing: "monthly",
    amount: 0, currency: "usd", days: 30,
    allowance: 0, deposit: 0, display_order: 999,
    description: "", notes: "", popular: false, active: true,
  });
  useEffect(() => {
    if (!dialog) return;
    if (dialog.kind === "edit" && dialog.doc) {
      setF({
        plan_id: dialog.doc.plan_id, name: dialog.doc.name,
        tier: dialog.doc.tier || "", billing: dialog.doc.billing || "monthly",
        amount: dialog.doc.amount, currency: dialog.doc.currency || "usd",
        days: dialog.doc.days, allowance: dialog.doc.allowance || 0,
        deposit: dialog.doc.deposit || 0,
        display_order: dialog.doc.display_order || 999,
        description: dialog.doc.description || "", notes: dialog.doc.notes || "",
        popular: !!dialog.doc.popular, active: dialog.doc.active !== false,
      });
    } else {
      setF({
        plan_id: "", name: "", tier: "", billing: "monthly",
        amount: 0, currency: "usd", days: 30,
        allowance: 0, deposit: 0, display_order: 999,
        description: "", notes: "", popular: false, active: true,
      });
    }
  }, [dialog]);
  if (!dialog) return null;
  const set = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    setF((s) => ({ ...s, [k]: v }));
  };
  const setNum = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value === "" ? "" : Number(e.target.value) }));
  const save = async () => {
    if (!f.plan_id.trim() || !f.name.trim()) { toast.error("plan_id and name are required."); return; }
    const payload = { ...f,
      amount: Number(f.amount), days: Number(f.days),
      allowance: Number(f.allowance || 0), deposit: Number(f.deposit || 0),
      display_order: Number(f.display_order || 999),
    };
    try {
      if (dialog.kind === "edit") {
        const { plan_id: _pid, ...patch } = payload;
        const r = await api.put(`/billing/admin/plans/${dialog.doc.id}`, patch);
        if (r.data?.versioned) {
          toast.success("New plan version created — existing subscribers stay on the old version.");
        } else {
          toast.success("Plan updated.");
        }
      } else {
        await api.post("/billing/admin/plans", payload);
        toast.success("Plan created.");
      }
      onSaved(); onClose();
    } catch (e) { toast.error(errMsg(e, "Couldn't save plan.")); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0d0d0d] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="plan-edit-dialog">
        <DialogHeader><DialogTitle className="font-display text-lg">
          {dialog.kind === "edit" ? `Edit — ${dialog.doc.name} (v${dialog.doc.version || 1})` : "New Plan"}
        </DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Plan ID (immutable)</label>
              <input data-testid="plan-plan-id" disabled={dialog.kind === "edit"} value={f.plan_id} onChange={set("plan_id")} placeholder="hobbyist_monthly" className={inputCls + (dialog.kind === "edit" ? " opacity-60" : "")} />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Display name</label>
              <input data-testid="plan-name" value={f.name} onChange={set("name")} placeholder="Hobbyist" className={inputCls} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Tier key</label>
              <input data-testid="plan-tier" value={f.tier} onChange={set("tier")} placeholder="hobbyist" className={inputCls} />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Billing</label>
              <select data-testid="plan-billing" value={f.billing} onChange={set("billing")} className={inputCls}>
                <option value="monthly">monthly</option>
                <option value="annual">annual</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Days</label>
              <input data-testid="plan-days" type="number" value={f.days} onChange={setNum("days")} className={inputCls} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Price ($)</label>
              <input data-testid="plan-amount" type="number" step="0.01" value={f.amount} onChange={setNum("amount")} className={inputCls} />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Monthly allowance</label>
              <input data-testid="plan-allowance" type="number" value={f.allowance} onChange={setNum("allowance")} className={inputCls} />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Annual deposit</label>
              <input data-testid="plan-deposit" type="number" value={f.deposit} onChange={setNum("deposit")} className={inputCls} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Currency</label>
              <input data-testid="plan-currency" value={f.currency} onChange={set("currency")} className={inputCls} />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Display order</label>
              <input data-testid="plan-order" type="number" value={f.display_order} onChange={setNum("display_order")} className={inputCls} />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Public description</label>
            <input data-testid="plan-desc" value={f.description} onChange={set("description")} className={inputCls} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Internal notes</label>
            <textarea data-testid="plan-notes" value={f.notes} onChange={set("notes")} rows={2} className={inputCls} />
          </div>
          <div className="flex items-center gap-4 pt-1">
            <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
              <input data-testid="plan-popular" type="checkbox" checked={f.popular} onChange={(e) => setF((s) => ({ ...s, popular: e.target.checked }))} /> Mark as popular
            </label>
            <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
              <input data-testid="plan-active" type="checkbox" checked={f.active} onChange={(e) => setF((s) => ({ ...s, active: e.target.checked }))} /> Active (public)
            </label>
          </div>
          <p className="text-[11px] text-amber-300/80 flex gap-1.5 items-start pt-1">
            <Warning size={13} className="mt-0.5 shrink-0" />
            <span>Changing price, allowance, deposit, days, billing or currency creates a NEW plan VERSION. Existing subscribers keep the old version — migrate them deliberately from the Subscribers view.</span>
          </p>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-zinc-400 hover:text-white hover:bg-white/10">Cancel</Button>
          <Button data-testid="plan-save-btn" onClick={save} className="bg-amber-400 text-black hover:bg-amber-300 font-bold">Save plan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Top-up editor dialog ----------------

function TopupDialog({ dialog, onClose, onSaved }) {
  const [f, setF] = useState({ pack_id: "", name: "", credits: 0, amount_full: 0, amount_member: 0, display_order: 0, notes: "", active: true });
  useEffect(() => {
    if (!dialog) return;
    if (dialog.kind === "edit" && dialog.doc) {
      setF({ ...dialog.doc, active: dialog.doc.active !== false });
    } else {
      setF({ pack_id: "", name: "", credits: 0, amount_full: 0, amount_member: 0, display_order: 0, notes: "", active: true });
    }
  }, [dialog]);
  if (!dialog) return null;
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const setNum = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value === "" ? "" : Number(e.target.value) }));
  const save = async () => {
    const payload = {
      pack_id: (f.pack_id || "").trim().toUpperCase(), name: f.name.trim(),
      credits: Number(f.credits || 0),
      amount_full: Number(f.amount_full || 0),
      amount_member: Number(f.amount_member || 0),
      display_order: Number(f.display_order || 0),
      notes: f.notes || "", active: !!f.active,
    };
    if (!payload.pack_id || !payload.name) { toast.error("pack_id and name are required."); return; }
    try {
      if (dialog.kind === "edit") {
        const { pack_id: _pid, ...patch } = payload;
        await api.put(`/billing/admin/topups/${dialog.doc.id}`, patch);
        toast.success("Top-up pack updated.");
      } else {
        await api.post("/billing/admin/topups", payload);
        toast.success("Top-up pack created.");
      }
      onSaved(); onClose();
    } catch (e) { toast.error(errMsg(e, "Couldn't save pack.")); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0d0d0d] border-white/10 text-white max-w-lg" data-testid="topup-edit-dialog">
        <DialogHeader><DialogTitle className="font-display text-lg">
          {dialog.kind === "edit" ? `Edit top-up — ${dialog.doc.name}` : "New Top-up Pack"}
        </DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Pack ID</label>
              <input data-testid="topup-pack-id" disabled={dialog.kind === "edit"} value={f.pack_id} onChange={set("pack_id")} placeholder="A" className={inputCls + (dialog.kind === "edit" ? " opacity-60" : "")} />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Credits</label>
              <input data-testid="topup-credits" type="number" value={f.credits} onChange={setNum("credits")} className={inputCls} />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Display name</label>
            <input data-testid="topup-name" value={f.name} onChange={set("name")} placeholder="50 Extra Credits" className={inputCls} />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Non-member ($)</label>
              <input data-testid="topup-amount-full" type="number" step="0.01" value={f.amount_full} onChange={setNum("amount_full")} className={inputCls} />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Member ($)</label>
              <input data-testid="topup-amount-member" type="number" step="0.01" value={f.amount_member} onChange={setNum("amount_member")} className={inputCls} />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Order</label>
              <input data-testid="topup-order" type="number" value={f.display_order} onChange={setNum("display_order")} className={inputCls} />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
            <input data-testid="topup-active" type="checkbox" checked={f.active} onChange={(e) => setF((s) => ({ ...s, active: e.target.checked }))} /> Active
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-zinc-400 hover:text-white hover:bg-white/10">Cancel</Button>
          <Button data-testid="topup-save-btn" onClick={save} className="bg-amber-400 text-black hover:bg-amber-300 font-bold">Save pack</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Gen-cost editor dialog ----------------

function GenCostDialog({ doc, onClose, onSaved }) {
  const [f, setF] = useState({ credit_cost: 1, pro_only: false, label: "", active: true });
  useEffect(() => { if (doc) setF({ credit_cost: doc.credit_cost, pro_only: !!doc.pro_only, label: doc.label || "", active: doc.active !== false }); }, [doc]);
  if (!doc) return null;
  const save = async () => {
    try {
      await api.put(`/billing/admin/gen-costs/${doc.id}`, {
        credit_cost: Number(f.credit_cost || 1),
        pro_only: !!f.pro_only, label: f.label || "",
        active: !!f.active,
      });
      toast.success("Generation cost updated.");
      onSaved(); onClose();
    } catch (e) { toast.error(errMsg(e, "Couldn't save cost.")); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0d0d0d] border-white/10 text-white max-w-md" data-testid="gencost-edit-dialog">
        <DialogHeader><DialogTitle className="font-display text-lg">Edit — {doc.tier_key.toUpperCase()} tier cost</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Display label</label>
            <input data-testid="gencost-label" value={f.label} onChange={(e) => setF((s) => ({ ...s, label: e.target.value }))} className={inputCls} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Credit cost per successful generation</label>
            <input data-testid="gencost-credit-cost" type="number" min="1" value={f.credit_cost} onChange={(e) => setF((s) => ({ ...s, credit_cost: Number(e.target.value) }))} className={inputCls} />
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
              <input data-testid="gencost-pro-only" type="checkbox" checked={f.pro_only} onChange={(e) => setF((s) => ({ ...s, pro_only: e.target.checked }))} /> Pro users only
            </label>
            <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
              <input data-testid="gencost-active" type="checkbox" checked={f.active} onChange={(e) => setF((s) => ({ ...s, active: e.target.checked }))} /> Active
            </label>
          </div>
          <p className="text-[11px] text-zinc-500">Quality and target pixels are set at the model level and not editable here. Deactivating a tier makes it unavailable for future generations.</p>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-zinc-400 hover:text-white hover:bg-white/10">Cancel</Button>
          <Button data-testid="gencost-save-btn" onClick={save} className="bg-amber-400 text-black hover:bg-amber-300 font-bold">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- User credit-adjustment dialog ----------------

function AdjustDialog({ target, onClose, onSaved }) {
  const [f, setF] = useState({ delta: 0, source: "wallet", event_type: "grant_manual", reason: "" });
  useEffect(() => { setF({ delta: 0, source: "wallet", event_type: "grant_manual", reason: "" }); }, [target]);
  if (!target) return null;
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));
  const walletAfter = f.source === "wallet" ? (target.wallet_balance + Number(f.delta || 0)) : target.wallet_balance;
  const monthlyUsedAfter = f.source === "monthly" ? Math.max(0, (target.monthly_used || 0) - Number(f.delta || 0)) : target.monthly_used;
  const save = async () => {
    if (!f.reason.trim() || f.reason.trim().length < 3) { toast.error("Reason is required (min 3 chars)."); return; }
    if (!f.delta || Number(f.delta) === 0) { toast.error("Delta must be non-zero."); return; }
    try {
      const r = await api.post(`/billing/admin/users/${target.user.user_id}/credits/adjust`, {
        delta: Number(f.delta), source: f.source, event_type: f.event_type, reason: f.reason,
      });
      toast.success("Adjustment recorded.");
      onSaved(r.data); onClose();
    } catch (e) { toast.error(errMsg(e, "Couldn't record adjustment.")); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0d0d0d] border-white/10 text-white max-w-md" data-testid="adjust-dialog">
        <DialogHeader><DialogTitle className="font-display text-lg">Adjust credits — {target.user.email}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3 text-xs text-zinc-400 space-y-1">
            <p><span className="text-zinc-500">Wallet balance:</span> <span className="text-white font-semibold">{target.wallet_balance}</span></p>
            <p><span className="text-zinc-500">Monthly used ({target.monthly_bucket_key}):</span> <span className="text-white font-semibold">{target.monthly_used}</span></p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Source</label>
              <select data-testid="adjust-source" value={f.source} onChange={set("source")} className={inputCls}>
                <option value="wallet">Wallet (persistent top-ups)</option>
                <option value="monthly">Monthly allowance</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Event type</label>
              <select data-testid="adjust-event-type" value={f.event_type} onChange={set("event_type")} className={inputCls}>
                <option value="grant_manual">Manual grant (support)</option>
                <option value="deduct_manual">Manual removal (support)</option>
                <option value="grant_promotional">Promotional</option>
                <option value="grant_refund">Refund</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Delta (positive = grant, negative = remove)</label>
            <input data-testid="adjust-delta" type="number" value={f.delta} onChange={set("delta")} className={inputCls} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Reason (mandatory)</label>
            <textarea data-testid="adjust-reason" value={f.reason} onChange={set("reason")} rows={2} className={inputCls} />
          </div>
          <div className="rounded-lg border border-amber-400/30 bg-amber-400/5 p-3 text-xs">
            <p className="text-amber-200 font-semibold mb-1">Preview</p>
            <p className="text-zinc-300">Wallet: <span className="font-bold text-white">{target.wallet_balance} → {walletAfter}</span></p>
            <p className="text-zinc-300">Monthly used: <span className="font-bold text-white">{target.monthly_used} → {monthlyUsedAfter}</span></p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-zinc-400 hover:text-white hover:bg-white/10">Cancel</Button>
          <Button data-testid="adjust-save-btn" onClick={save} className="bg-amber-400 text-black hover:bg-amber-300 font-bold">Record adjustment</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Main page ----------------

const TABS = [
  { key: "plans", label: "Plans", icon: Package },
  { key: "topups", label: "Top-up Packs", icon: Coins },
  { key: "costs", label: "Generation Costs", icon: ChartLine },
  { key: "users", label: "Users & Ledger", icon: Users },
];

export default function AdminBilling() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [tab, setTab] = useState("plans");
  const [plans, setPlans] = useState(null);
  const [topups, setTopups] = useState(null);
  const [costs, setCosts] = useState(null);
  const [planDialog, setPlanDialog] = useState(null);
  const [topupDialog, setTopupDialog] = useState(null);
  const [costDialog, setCostDialog] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadPlans = useCallback(async () => {
    try { const r = await api.get("/billing/admin/plans"); setPlans(r.data.plans || []); }
    catch (e) { toast.error(errMsg(e, "Couldn't load plans.")); }
  }, []);
  const loadTopups = useCallback(async () => {
    try { const r = await api.get("/billing/admin/topups"); setTopups(r.data.topups || []); }
    catch (e) { toast.error(errMsg(e, "Couldn't load top-ups.")); }
  }, []);
  const loadCosts = useCallback(async () => {
    try { const r = await api.get("/billing/admin/gen-costs"); setCosts(r.data.gen_costs || []); }
    catch (e) { toast.error(errMsg(e, "Couldn't load costs.")); }
  }, []);

  useEffect(() => {
    if (!user?.is_admin) return;
    if (tab === "plans") loadPlans();
    else if (tab === "topups") loadTopups();
    else if (tab === "costs") loadCosts();
  }, [tab, user, loadPlans, loadTopups, loadCosts]);

  if (!authLoading && user && !user.is_admin) { navigate("/dashboard"); return null; }

  const deactivatePlan = async (doc) => {
    if (!window.confirm(`Deactivate "${doc.name}"? Existing subscribers keep their access; new purchases will fail.`)) return;
    setBusy(true);
    try { await api.delete(`/billing/admin/plans/${doc.id}`); toast.success("Plan deactivated."); loadPlans(); }
    catch (e) { toast.error(errMsg(e, "Couldn't deactivate.")); }
    finally { setBusy(false); }
  };
  const deactivateTopup = async (doc) => {
    if (!window.confirm(`Deactivate "${doc.name}"?`)) return;
    setBusy(true);
    try { await api.delete(`/billing/admin/topups/${doc.id}`); toast.success("Pack deactivated."); loadTopups(); }
    catch (e) { toast.error(errMsg(e, "Couldn't deactivate.")); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-billing" className="min-h-screen bg-[#070707] text-white font-body">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/60 border-b border-white/10">
        <div className="max-w-[1240px] mx-auto px-6 h-16 flex items-center gap-3">
          <button onClick={() => navigate("/dashboard")} data-testid="billing-back-btn" className="h-9 w-9 rounded-lg hover:bg-white/10 flex items-center justify-center transition-colors"><ArrowLeft size={18} /></button>
          <span className="font-display font-extrabold text-lg tracking-tight">Billing & Credits</span>
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-500/15 text-red-300 ring-1 ring-red-400/30">ADMIN</span>
        </div>
      </header>

      <main className="max-w-[1240px] mx-auto px-6 py-8 space-y-6">
        {/* Tabs */}
        <div className="flex gap-1 border-b border-white/10">
          {TABS.map((t) => {
            const Ico = t.icon;
            const active = t.key === tab;
            return (
              <button key={t.key} data-testid={`billing-tab-${t.key}`} onClick={() => setTab(t.key)}
                className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-semibold transition-colors ${active ? "text-white border-b-2 border-amber-400" : "text-zinc-400 hover:text-white"}`}>
                <Ico weight={active ? "fill" : "regular"} size={16} /> {t.label}
              </button>
            );
          })}
        </div>

        {tab === "plans" && (
          <PlansTab plans={plans} onEdit={(doc) => setPlanDialog({ kind: "edit", doc })}
                    onNew={() => setPlanDialog({ kind: "new" })}
                    onDeactivate={deactivatePlan} onRefresh={loadPlans} busy={busy} />
        )}
        {tab === "topups" && (
          <TopupsTab topups={topups} onEdit={(doc) => setTopupDialog({ kind: "edit", doc })}
                     onNew={() => setTopupDialog({ kind: "new" })}
                     onDeactivate={deactivateTopup} busy={busy} />
        )}
        {tab === "costs" && (
          <CostsTab costs={costs} onEdit={setCostDialog} />
        )}
        {tab === "users" && (
          <UsersTab />
        )}
      </main>

      <PlanDialog dialog={planDialog} onClose={() => setPlanDialog(null)} onSaved={loadPlans} />
      <TopupDialog dialog={topupDialog} onClose={() => setTopupDialog(null)} onSaved={loadTopups} />
      <GenCostDialog doc={costDialog} onClose={() => setCostDialog(null)} onSaved={loadCosts} />
    </div>
  );
}

// ---------------- Plans tab ----------------

function PlansTab({ plans, onEdit, onNew, onDeactivate, onRefresh, busy }) {
  const [showRetired, setShowRetired] = useState(false);
  const [subscribersFor, setSubscribersFor] = useState(null);
  const [syncFor, setSyncFor] = useState(null);
  if (plans === null) return <p className="text-sm text-zinc-500 py-10 text-center">Loading…</p>;
  const list = plans.filter((p) => showRetired || p.active !== false);
  const grouped = list.reduce((acc, p) => { (acc[p.plan_id] = acc[p.plan_id] || []).push(p); return acc; }, {});
  return (
    <section data-testid="plans-tab" className="space-y-4">
      <div className="flex items-center gap-2">
        <p className="text-xs text-zinc-500">{list.length} plan {list.length === 1 ? "row" : "rows"}</p>
        <label className="ml-auto flex items-center gap-2 text-xs text-zinc-400 cursor-pointer">
          <input data-testid="plans-show-retired" type="checkbox" checked={showRetired} onChange={(e) => setShowRetired(e.target.checked)} />
          Show retired versions
        </label>
        <button data-testid="plans-refresh-btn" onClick={onRefresh} className="h-8 w-8 rounded-lg hover:bg-white/10 flex items-center justify-center"><ArrowsClockwise size={14} /></button>
        <button data-testid="plans-new-btn" onClick={onNew} className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full bg-amber-400 text-black hover:bg-amber-300 text-sm font-bold transition-colors"><Plus size={14} weight="bold" /> New Plan</button>
      </div>
      {Object.keys(grouped).map((pid) => (
        <div key={pid} className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
          <div className="flex items-center gap-3 mb-2">
            <p className="font-display font-bold text-[15px]">{pid}</p>
            <button data-testid={`plan-view-subs-${pid}`} onClick={() => setSubscribersFor(grouped[pid].find((p) => p.active !== false) || grouped[pid][0])} className="text-[11px] text-amber-300 hover:text-amber-200 underline">View subscribers…</button>
          </div>
          <div className="divide-y divide-white/5">
            {grouped[pid].map((p) => (
              <div key={p.id} data-testid={`plan-row-${p.id}`} className="py-2.5 flex items-center gap-3 text-sm flex-wrap">
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${p.active !== false ? "bg-emerald-500/20 text-emerald-300" : "bg-zinc-600/30 text-zinc-500"}`}>{p.active !== false ? "ACTIVE" : "RETIRED"}</span>
                <span className="text-zinc-400 text-xs">v{p.version || 1}</span>
                <span className="text-white font-semibold">{p.name}</span>
                <span className="text-zinc-500 text-xs">{p.billing} · {p.days}d</span>
                <span className="text-zinc-300 font-mono">${money(p.amount)}</span>
                <span className="text-amber-300 text-xs">{p.allowance ? `${p.allowance} cr/mo` : p.deposit ? `${p.deposit} cr up front` : "—"}</span>
                <SyncBadge plan={p} />
                <span className="text-zinc-600 text-[10px] ml-auto">upd {dt(p.updated_at)}</span>
                {p.active !== false && ["monthly", "annual"].includes(p.billing) && (
                  <button data-testid={`plan-sync-${p.id}`} onClick={() => setSyncFor(p)}
                    className={`inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-[11px] font-bold transition-colors ${
                      p.stripe_price_id
                        ? "bg-white/5 text-zinc-300 hover:bg-white/10"
                        : "bg-amber-400/15 text-amber-300 hover:bg-amber-400/25"
                    }`}>
                    <CloudArrowUp size={12} weight="bold" /> {p.stripe_price_id ? "Re-sync" : "Sync to Stripe"}
                  </button>
                )}
                <button data-testid={`plan-edit-${p.id}`} onClick={() => onEdit(p)} className="h-7 w-7 rounded-full hover:bg-white/10 flex items-center justify-center text-zinc-400"><PencilSimple size={13} /></button>
                {p.active !== false && (
                  <button data-testid={`plan-deactivate-${p.id}`} disabled={busy} onClick={() => onDeactivate(p)} className="h-7 w-7 rounded-full hover:bg-red-500/20 flex items-center justify-center text-zinc-500 hover:text-red-300"><X size={13} weight="bold" /></button>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
      {subscribersFor && <SubscribersDialog plan={subscribersFor} allPlans={plans} onClose={() => setSubscribersFor(null)} />}
      {syncFor && <SyncToStripeDialog plan={syncFor} onClose={() => setSyncFor(null)} onSynced={onRefresh} />}
    </section>
  );
}

function SyncBadge({ plan }) {
  if (plan.active === false) {
    return <span data-testid={`plan-sync-badge-${plan.id}`} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-zinc-600/30 text-zinc-500">Retired</span>;
  }
  if (!plan.stripe_price_id) {
    return <span data-testid={`plan-sync-badge-${plan.id}`} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-zinc-700/40 text-zinc-300 ring-1 ring-white/10">Not Synced</span>;
  }
  const env = (plan.stripe_env || "").toLowerCase();
  if (env === "test") {
    return <span data-testid={`plan-sync-badge-${plan.id}`} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-300 ring-1 ring-blue-400/30">Stripe Test</span>;
  }
  if (env === "live") {
    return <span data-testid={`plan-sync-badge-${plan.id}`} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-400/30"><Lightning size={9} weight="fill" /> Stripe Live</span>;
  }
  return <span data-testid={`plan-sync-badge-${plan.id}`} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 ring-1 ring-amber-400/30">Synced</span>;
}

function SyncToStripeDialog({ plan, onClose, onSynced }) {
  const [confirming, setConfirming] = useState(!plan.stripe_price_id);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  // Determine target env from the current backend Stripe key. The `stripe_env`
  // field on any already-synced plan gives us a strong hint; otherwise we
  // treat it as "current environment" and defer to what the sync endpoint
  // reports back after the call.
  const [envHint, setEnvHint] = useState(plan.stripe_env || null);
  useEffect(() => {
    if (envHint) return;
    // If we don't know the env yet, ask the server via list_plans (any synced sibling reveals it).
    api.get("/billing/admin/plans").then((r) => {
      const anySynced = (r.data.plans || []).find((p) => p.stripe_env);
      if (anySynced) setEnvHint(anySynced.stripe_env);
    }).catch(() => {});
  }, [envHint]);
  const isReSync = !!plan.stripe_price_id;
  const doSync = async () => {
    setBusy(true); setError(null);
    try {
      const r = await api.post(`/billing/admin/plans/${plan.id}/sync-to-stripe`);
      setResult(r.data);
      toast.success(isReSync ? "Plan re-synced with Stripe." : `Plan synced to Stripe ${r.data.stripe_env}.`);
      onSynced && onSynced();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Sync failed.";
      setError(typeof msg === "string" ? msg : "Sync failed.");
    } finally {
      setBusy(false);
    }
  };
  const isLive = (envHint || "").toLowerCase() === "live";
  const isTest = (envHint || "").toLowerCase() === "test";
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0d0d0d] border-white/10 text-white max-w-lg" data-testid="sync-stripe-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-lg inline-flex items-center gap-2">
            <CloudArrowUp size={20} weight="fill" className="text-amber-300" />
            {isReSync ? "Re-sync to Stripe" : "Sync to Stripe"}
          </DialogTitle>
        </DialogHeader>
        {!result && (
          <div className="space-y-3">
            <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3 text-sm">
              <div className="flex items-baseline gap-2">
                <span className="font-display font-bold">{plan.name}</span>
                <span className="text-[10px] text-zinc-500">v{plan.version || 1}</span>
              </div>
              <div className="text-xs text-zinc-400 mt-1">
                <span className="font-mono text-white">${money(plan.amount)}</span> <span className="uppercase">{plan.currency}</span>{" · "}
                <span className="capitalize">{plan.billing}</span> billing
                {plan.allowance ? <> · {plan.allowance} cr/mo</> : plan.deposit ? <> · {plan.deposit} cr upfront</> : null}
              </div>
              <div className="text-[11px] text-zinc-500 mt-1 font-mono truncate">{plan.plan_id} · {plan.id}</div>
            </div>
            {isLive && !isReSync && (
              <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-xs text-red-200">
                <p className="font-bold uppercase tracking-wider text-[10px] mb-1 inline-flex items-center gap-1">
                  <Warning size={12} weight="fill" /> Live Stripe environment
                </p>
                This will create a REAL Stripe Product and recurring Price against your LIVE account.
                Existing subscribers on other plan versions are unaffected. Only new purchasers of this
                version will be charged the recurring price.
              </div>
            )}
            {isTest && !isReSync && (
              <div className="rounded-lg border border-blue-500/30 bg-blue-500/10 p-3 text-xs text-blue-200">
                <p className="font-bold uppercase tracking-wider text-[10px] mb-1">Test Stripe environment</p>
                This will create Stripe Test Product + Price. No real money changes hands.
              </div>
            )}
            {!envHint && (
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200">
                <p className="font-bold uppercase tracking-wider text-[10px] mb-1 inline-flex items-center gap-1">
                  <Warning size={12} weight="fill" /> Environment unknown
                </p>
                The current Stripe environment isn&apos;t detectable yet. The server will use whatever
                key is configured. Confirm only if you&apos;re certain the backend is pointed at the intended environment.
              </div>
            )}
            {isReSync && (
              <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3 text-xs text-zinc-300">
                Existing IDs: <span className="font-mono">{plan.stripe_product_id?.slice(0, 12)}…</span> / <span className="font-mono">{plan.stripe_price_id?.slice(0, 12)}…</span>
                <br />Re-sync is idempotent — the same Stripe objects will be reused; nothing new is created.
              </div>
            )}
            {error && (
              <div data-testid="sync-error" className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-xs text-red-200">
                {error}
              </div>
            )}
            {isLive && !isReSync && (
              <label className="flex items-start gap-2 text-xs text-zinc-300 cursor-pointer select-none">
                <input data-testid="sync-live-confirm" type="checkbox" checked={confirming} onChange={(e) => setConfirming(e.target.checked)} className="mt-0.5" />
                <span>I understand this creates LIVE Stripe objects and will affect future customer checkouts of this plan version.</span>
              </label>
            )}
          </div>
        )}
        {result && (
          <div data-testid="sync-result" className="space-y-2 text-sm">
            <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3">
              <p className="font-bold uppercase tracking-wider text-[10px] text-emerald-300 inline-flex items-center gap-1"><Check size={12} weight="bold" /> Synced to Stripe ({result.stripe_env})</p>
              <div className="text-xs text-zinc-300 mt-2 space-y-1 font-mono">
                <div>Product: <span className="text-white">{result.stripe_product_id}</span></div>
                <div>Price&nbsp;&nbsp; : <span className="text-white">{result.stripe_price_id}</span></div>
              </div>
            </div>
            <p className="text-[11px] text-zinc-500">Future checkouts of this plan version will now open Stripe Checkout in <span className="text-zinc-200 font-bold">subscription</span> mode. Existing customers on other versions are unaffected.</p>
          </div>
        )}
        <DialogFooter>
          {!result ? (
            <>
              <Button variant="ghost" onClick={onClose} className="text-zinc-400 hover:text-white hover:bg-white/10">Cancel</Button>
              <Button
                data-testid="sync-confirm-btn"
                onClick={doSync}
                disabled={busy || (isLive && !isReSync && !confirming)}
                className="bg-amber-400 text-black hover:bg-amber-300 font-bold disabled:opacity-50"
              >
                {busy ? <><CircleNotch size={14} className="animate-spin mr-1.5" /> Syncing…</> : (isReSync ? "Re-sync now" : "Sync now")}
              </Button>
            </>
          ) : (
            <Button data-testid="sync-close-btn" onClick={onClose} className="bg-white text-black hover:bg-zinc-100 font-bold">Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SubscribersDialog({ plan, allPlans, onClose }) {
  const [rows, setRows] = useState(null);
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/billing/admin/plans/${plan.id}/subscribers`).then((r) => setRows(r.data.subscribers || [])).catch(() => setRows([]));
  }, [plan]);
  const siblings = allPlans.filter((p) => p.plan_id === plan.plan_id && p.id !== plan.id);
  const migrate = async (userId) => {
    if (!target) { toast.error("Choose a target plan version."); return; }
    setBusy(true);
    try {
      await api.post(`/billing/admin/users/${userId}/plan/migrate`, { plan_version_id: target, reason: "Manual migration" });
      toast.success("User migrated.");
      const r = await api.get(`/billing/admin/plans/${plan.id}/subscribers`);
      setRows(r.data.subscribers || []);
    } catch (e) { toast.error(errMsg(e, "Migration failed.")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0d0d0d] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="subscribers-dialog">
        <DialogHeader><DialogTitle className="font-display text-lg">Subscribers on “{plan.name}” v{plan.version || 1}</DialogTitle></DialogHeader>
        <div className="text-xs text-zinc-500 mb-3">
          These users are locked to this plan version. Migrations are deliberate — pick a target version and hit Migrate. Their expires_at and current balances are preserved.
        </div>
        {siblings.length > 0 && (
          <div className="mb-3">
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Migrate to</label>
            <select data-testid="subscribers-target-version" value={target} onChange={(e) => setTarget(e.target.value)} className={inputCls}>
              <option value="">— pick a target version —</option>
              {siblings.map((s) => <option key={s.id} value={s.id}>{s.name} · v{s.version || 1} · ${money(s.amount)} · {s.active !== false ? "active" : "retired"}</option>)}
            </select>
          </div>
        )}
        {rows === null ? <p className="text-sm text-zinc-500 py-6 text-center">Loading…</p> : rows.length === 0 ? (
          <p className="text-sm text-zinc-500 py-6 text-center">No subscribers on this plan version.</p>
        ) : (
          <div className="divide-y divide-white/5">
            {rows.map((r) => (
              <div key={r.user_id} data-testid={`subscriber-row-${r.user_id}`} className="py-2.5 flex items-center gap-3 text-sm">
                <span className="text-white truncate">{r.email}</span>
                <span className="text-zinc-500 text-xs truncate">{r.name}</span>
                <span className="ml-auto text-[11px] text-zinc-500">exp {dt(r.subscription?.expires_at)}</span>
                <button data-testid={`subscriber-migrate-${r.user_id}`} disabled={busy || !target} onClick={() => migrate(r.user_id)} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-400/15 hover:bg-amber-400/25 text-amber-300 disabled:opacity-50 text-[11px] font-semibold">Migrate</button>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Top-ups tab ----------------

function TopupsTab({ topups, onEdit, onNew, onDeactivate, busy }) {
  if (topups === null) return <p className="text-sm text-zinc-500 py-10 text-center">Loading…</p>;
  return (
    <section data-testid="topups-tab" className="space-y-3">
      <div className="flex items-center gap-2">
        <p className="text-xs text-zinc-500">{topups.length} pack{topups.length === 1 ? "" : "s"}</p>
        <button data-testid="topups-new-btn" onClick={onNew} className="ml-auto inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full bg-amber-400 text-black hover:bg-amber-300 text-sm font-bold transition-colors"><Plus size={14} weight="bold" /> New Pack</button>
      </div>
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] divide-y divide-white/5">
        {topups.map((t) => (
          <div key={t.id} data-testid={`topup-row-${t.id}`} className="p-4 flex items-center gap-3 text-sm">
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${t.active !== false ? "bg-emerald-500/20 text-emerald-300" : "bg-zinc-600/30 text-zinc-500"}`}>{t.active !== false ? "ACTIVE" : "INACTIVE"}</span>
            <span className="font-display font-bold text-[13px] w-8">{t.pack_id}</span>
            <span className="text-white">{t.name}</span>
            <span className="text-amber-300">+{t.credits} cr</span>
            <span className="text-zinc-500 text-xs ml-auto">non-member <span className="text-white font-mono">${money(t.amount_full)}</span> · member <span className="text-white font-mono">${money(t.amount_member)}</span></span>
            <button data-testid={`topup-edit-${t.id}`} onClick={() => onEdit(t)} className="h-7 w-7 rounded-full hover:bg-white/10 flex items-center justify-center text-zinc-400"><PencilSimple size={13} /></button>
            {t.active !== false && <button data-testid={`topup-deactivate-${t.id}`} disabled={busy} onClick={() => onDeactivate(t)} className="h-7 w-7 rounded-full hover:bg-red-500/20 flex items-center justify-center text-zinc-500 hover:text-red-300"><X size={13} weight="bold" /></button>}
          </div>
        ))}
      </div>
    </section>
  );
}

// ---------------- Gen costs tab ----------------

function CostsTab({ costs, onEdit }) {
  if (costs === null) return <p className="text-sm text-zinc-500 py-10 text-center">Loading…</p>;
  return (
    <section data-testid="costs-tab" className="space-y-3">
      <p className="text-xs text-zinc-500">Credit cost charged per successful generation. Failed generations never consume credits.</p>
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] divide-y divide-white/5">
        {costs.map((c) => (
          <div key={c.id} data-testid={`cost-row-${c.id}`} className="p-4 flex items-center gap-3 text-sm">
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${c.active !== false ? "bg-emerald-500/20 text-emerald-300" : "bg-zinc-600/30 text-zinc-500"}`}>{c.active !== false ? "ACTIVE" : "INACTIVE"}</span>
            <span className="font-display font-bold uppercase w-10">{c.tier_key}</span>
            <span className="text-white">{c.label}</span>
            <span className="text-zinc-500 text-xs">{c.quality} · {(c.target_px / 1_000_000).toFixed(1)} MP</span>
            <span className="ml-auto text-amber-300 font-bold">{c.credit_cost} credit{c.credit_cost === 1 ? "" : "s"}</span>
            {c.pro_only && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-purple-500/20 text-purple-300">PRO</span>}
            <button data-testid={`cost-edit-${c.tier_key}`} onClick={() => onEdit(c)} className="h-7 w-7 rounded-full hover:bg-white/10 flex items-center justify-center text-zinc-400"><PencilSimple size={13} /></button>
          </div>
        ))}
      </div>
    </section>
  );
}

// ---------------- Users & ledger tab ----------------

function UsersTab() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [history, setHistory] = useState(null);
  const [adjustTarget, setAdjustTarget] = useState(null);

  const LIMIT = 25;
  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  const runSearch = useCallback(async (nextQ, nextPage) => {
    setSearching(true);
    try {
      const r = await api.get(`/billing/admin/users/search`, {
        params: { q: nextQ, page: nextPage, limit: LIMIT },
      });
      setResults(r.data.users || []);
      setTotal(Number(r.data.total || 0));
    } catch (e) {
      toast.error(errMsg(e, "Search failed."));
    } finally {
      setSearching(false);
    }
  }, []);

  // Debounced live search — resets page to 1 on every query change so
  // deep matches surface immediately.
  useEffect(() => {
    setPage(1);
    const t = setTimeout(() => { runSearch(q, 1); }, 250);
    return () => clearTimeout(t);
  }, [q, runSearch]);

  // Page changes (user clicks Previous/Next) — same query, different page.
  const goToPage = (nextPage) => {
    const clamped = Math.max(1, Math.min(totalPages, nextPage));
    if (clamped === page) return;
    setPage(clamped);
    runSearch(q, clamped);
  };

  const openUser = async (u) => {
    setSelected(u); setDetail(null); setHistory(null);
    try {
      const [d, h] = await Promise.all([
        api.get(`/billing/admin/users/${u.user_id}/billing`),
        api.get(`/billing/admin/users/${u.user_id}/credit-history`, { params: { limit: 100 } }),
      ]);
      setDetail(d.data); setHistory(h.data);
    } catch (e) { toast.error(errMsg(e, "Couldn't load user detail.")); }
  };

  return (
    <section data-testid="users-tab" className="grid lg:grid-cols-[340px_1fr] gap-6">
      <div>
        <div className="relative mb-3">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input data-testid="users-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search email, name, user_id…"
                 className={inputCls + " pl-9"} />
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] divide-y divide-white/5 max-h-[70vh] overflow-y-auto">
          {results.length === 0 ? (
            <p className="p-4 text-xs text-zinc-500">
              {searching ? "Searching…" : "No users match."}
            </p>
          ) : results.map((u) => (
            <button key={u.user_id} data-testid={`user-row-${u.user_id}`} onClick={() => openUser(u)}
              className={`w-full text-left p-3 flex flex-col hover:bg-white/5 ${selected?.user_id === u.user_id ? "bg-white/5" : ""}`}>
              <span className="text-sm text-white truncate">{u.email}</span>
              <span className="text-[11px] text-zinc-500 truncate">{u.name || "—"}</span>
              <span className="text-[10px] text-zinc-600">wallet {u.topup_credits || 0} · {u.subscription?.plan_id || "free"}</span>
            </button>
          ))}
        </div>

        {/* Pagination controls */}
        {total > 0 && (
          <div data-testid="users-pagination" className="mt-3 flex items-center gap-2 text-[11px] text-zinc-400">
            <button
              data-testid="users-page-prev"
              onClick={() => goToPage(page - 1)}
              disabled={page <= 1 || searching}
              className="inline-flex items-center h-8 px-3 rounded-full bg-white/5 hover:bg-white/10 disabled:opacity-40 disabled:hover:bg-white/5 text-white font-semibold transition-colors"
            >
              Previous
            </button>
            <span data-testid="users-page-indicator" className="flex-1 text-center">
              Page <span className="text-white font-bold">{page}</span> of <span className="text-white font-bold">{totalPages}</span>
              <span className="text-zinc-600"> · {total} total</span>
            </span>
            <button
              data-testid="users-page-next"
              onClick={() => goToPage(page + 1)}
              disabled={page >= totalPages || searching}
              className="inline-flex items-center h-8 px-3 rounded-full bg-white/5 hover:bg-white/10 disabled:opacity-40 disabled:hover:bg-white/5 text-white font-semibold transition-colors"
            >
              Next
            </button>
          </div>
        )}
      </div>

      <div>
        {!selected ? (
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-8 text-sm text-zinc-500 text-center">Select a user to view billing detail, credit history, and record adjustments.</div>
        ) : !detail ? (
          <p className="text-sm text-zinc-500 py-6 text-center"><CircleNotch size={16} className="inline animate-spin mr-2" /> Loading…</p>
        ) : (
          <div className="space-y-4">
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  <p className="font-display font-bold text-lg">{detail.user.email}</p>
                  <p className="text-xs text-zinc-500">{detail.user.name} · <span className="font-mono">{detail.user.user_id}</span></p>
                </div>
                <button data-testid="user-adjust-btn" onClick={() => setAdjustTarget(detail)}
                  className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full bg-amber-400 text-black hover:bg-amber-300 text-sm font-bold"><Plus size={13} weight="bold" /> Adjust credits</button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                <StatCard label="Plan" value={detail.subscription?.plan_id || "free"} sub={detail.subscription?.billing || ""} />
                <StatCard label="Expires" value={dt(detail.subscription?.expires_at)} sub={detail.subscription?.tier ? `tier: ${detail.subscription.tier}` : ""} />
                <StatCard label="Wallet balance" value={detail.wallet_balance} sub="persistent top-up credits" />
                <StatCard label={`Monthly used (${detail.monthly_bucket_key})`} value={detail.monthly_used} sub="allowance consumed" />
              </div>
              {detail.plan_version_snapshot && (
                <p className="text-[11px] text-zinc-500 mt-3">Locked to plan version <span className="text-zinc-300 font-mono">{detail.plan_version_snapshot.id.slice(0,8)}</span> — <span className="text-white font-semibold">${money(detail.plan_version_snapshot.amount)}</span> · {detail.plan_version_snapshot.allowance || detail.plan_version_snapshot.deposit} credits</p>
              )}
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
              <p className="font-display font-bold text-[13px] mb-2">Credit history <span className="text-zinc-500 font-normal text-xs">({history?.total || 0} total)</span></p>
              {!history?.entries?.length ? <p className="text-xs text-zinc-500 py-4 text-center">No ledger entries yet.</p> : (
                <div className="divide-y divide-white/5 max-h-[42vh] overflow-y-auto">
                  {history.entries.map((e) => (
                    <div key={e.id} data-testid={`ledger-row-${e.id}`} className="py-2 flex items-start gap-3 text-xs">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold shrink-0 ${e.delta >= 0 ? "bg-emerald-500/20 text-emerald-300" : "bg-red-500/15 text-red-300"}`}>{e.delta > 0 ? `+${e.delta}` : e.delta}</span>
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[9px] font-bold shrink-0 ${e.source === "wallet" ? "bg-blue-500/20 text-blue-300" : "bg-purple-500/20 text-purple-300"}`}>{e.source}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-white truncate">{e.event_type}</p>
                        <p className="text-zinc-500 text-[10px] truncate">{e.reason}</p>
                      </div>
                      <span className="text-zinc-600 text-[10px] shrink-0">{dt(e.created_at)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
              <p className="font-display font-bold text-[13px] mb-2">Recent transactions</p>
              {!detail.recent_transactions?.length ? <p className="text-xs text-zinc-500 py-4 text-center">No payment transactions yet.</p> : (
                <div className="divide-y divide-white/5 max-h-[24vh] overflow-y-auto">
                  {detail.recent_transactions.map((tx) => (
                    <div key={tx.id} className="py-2 flex items-center gap-3 text-xs">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${tx.payment_status === "paid" ? "bg-emerald-500/20 text-emerald-300" : "bg-zinc-600/30 text-zinc-400"}`}>{tx.payment_status || "?"}</span>
                      <span className="text-white">${money(tx.amount)}</span>
                      <span className="text-zinc-500">{tx.plan_id || tx.pack_id}</span>
                      <span className="text-zinc-500">{tx.provider}</span>
                      <span className="text-zinc-600 ml-auto">{dt(tx.created_at)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <AdjustDialog target={adjustTarget} onClose={() => setAdjustTarget(null)} onSaved={() => selected && openUser(selected)} />
    </section>
  );
}

function StatCard({ label, value, sub }) {
  return (
    <div className="rounded-xl border border-white/10 bg-black/30 p-3">
      <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{label}</p>
      <p className="font-display font-bold text-lg text-white truncate">{value}</p>
      {sub && <p className="text-[10px] text-zinc-500 truncate">{sub}</p>}
    </div>
  );
}
